/**
 * SubcollectionFieldButton — F12 PR 2 pkt 2 iter 2a (2026-05-19).
 *
 * Drag source dla pola helper-encji w palecie po lewej canvas zakładki subcollection.
 * Analogiczne do `PaletteFieldButton` (private w SystemModuleBuilderPage), ale generic —
 * przyjmuje minimalną definicję pola (id/slug/name/fieldType) bez `SystemModuleField`
 * coupling z głównego buildera.
 *
 * Drag data shape (`PaletteDragData`):
 *   { source: 'palette', kind: 'field', payload: field.id }
 *
 * Drop target = sekcja w canvasie (handled by `usePaletteDnD.onAddFromPalette`).
 *
 * Click fallback = `onClick` callback (zachowane dla a11y, jak w `PaletteFieldButton`).
 */

import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';

import { type PaletteDragData } from '@/hooks/usePaletteDnD';

export interface SubcollectionFieldButtonField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
}

export interface SubcollectionFieldButtonProps {
  field: SubcollectionFieldButtonField;
  onClick?: () => void;
  disabled?: boolean;
  /** Optional custom test id prefix (default `subcollection-palette-field-`). */
  testIdPrefix?: string;
}

export function SubcollectionFieldButton({
  field,
  onClick,
  disabled = false,
  testIdPrefix = 'subcollection-palette-field-',
}: SubcollectionFieldButtonProps) {
  const { t } = useTranslation();

  const dragData: PaletteDragData = {
    source: 'palette',
    kind: 'field',
    payload: field.id,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-field-${field.id}`,
    data: dragData,
    disabled,
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...attributes}
      {...listeners}
      className={[
        'group flex w-full cursor-grab items-center gap-2 rounded border border-border-primary bg-bg-secondary px-2 py-1.5 text-left text-xs transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:border-accent-400 hover:bg-bg-tertiary active:cursor-grabbing',
        isDragging ? 'opacity-50' : '',
      ].join(' ')}
      aria-label={t('tenant.subcollectionLayout.v4.dragFieldAria', {
        name: field.name,
        defaultValue: `Przeciągnij pole „${field.name}" na sekcję`,
      })}
      data-testid={`${testIdPrefix}${field.slug}`}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-text-primary">{field.name}</span>
        <span className="block truncate text-[10px] text-text-tertiary">
          {field.slug} · {field.fieldType}
        </span>
      </span>
    </button>
  );
}
